import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { subscribePaginated } from '@adornis/baseql/server/resolver/mongo.js';
import { watchEntireCollection, watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import type { PaymentStatus, SubscriptionStatus } from '@adornis/payments/db/payments.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { DateTime } from 'luxon';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { bhBaseQlContext } from '../bh-context.js';
import { BHFilter } from '../entities/bh-filter.js';
import { BHPayment } from '../entities/BHPayment.js';
import { Certificate } from '../entities/certificate.js';
import { Participation } from '../entities/Participation.js';
import { AppointmentParticipation, ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { ProductParticipationConfirmationLink } from '../entities/ProductParticipationConfirmationLink.js';
import { Product } from '../entities/products.js';
import { TextMultipleLangItem } from '../entities/TextMultipleLangItem.js';
import { User } from '../entities/user.js';
import { WorkshopAppointmentParticipationConfirmationLink } from '../entities/WorkshopAppointmentParticipationConfirmationLink.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { BlockSignoffDaysInAdvanceError } from '../errors/BlockSignoffDaysInAdvanceError.js';
import { notifyAbmeldung } from '../notification/abmeldung.js';
import { notifyNuzterTeilnahmeAufgehoben } from '../notification/nutzer-teilnahme-aufgehoben.js';
import { notifyNuzterTeilnahme } from '../notification/nutzer-teilnahme.js';
import { getPluginForScope } from '../plugins/plugin.js';
import {
  getAppointmentParticipationConformationSecret,
  getParticipationConformationSecret,
  workshopParticipationFullfillsParticipationRequirements,
} from '../server/logic/participation.js';
import { searchMatch } from '../util.js';
import { Workshop } from './../entities/workshop.js';

async function setAppointmentParticipationServer(participationID: string, appointmentID: string, fullfilled: boolean) {
  const participation = await getByID(
    ParticipationWorkshop,
    participationID,
  )({
    _id: 1,
    fulfilledParticipationRequirements: 1,
    state: 1,
    appointmentParticipations: { _id: 1, appointmentID: 1 },
    product: { settings: { extraParticipationConstraints: 1 }, appointments: { _id: 1, isNonBlocking: 1 } },
  });
  if (!participation) throw new Error(`No WorkshopParticipation to participate in found (${participationID})`);
  const appointmentParticipations = participation.appointmentParticipations ?? [];

  if (fullfilled) {
    const appointmenentParticipation = new AppointmentParticipation({ appointmentID });
    appointmentParticipations.push(appointmenentParticipation);
  }

  if (!fullfilled) {
    const index = appointmentParticipations.findIndex(it => it.appointmentID === appointmentID);
    if (index === -1) throw new Error('appointmentParticipation not found');
    appointmentParticipations.splice(index, 1);
  }

  participation.appointmentParticipations = appointmentParticipations;
  await participation.save();

  // maybe update state (and trigger sideeffects)
  if (bhBaseQlContext.participationStateChangeWasInitiatedByAdmin) return true; // admin should be able to set state manually
  if (participation.state === ParticipationState.PARTICIPATED || participation.state === ParticipationState.IN) {
    const requirementsMet = workshopParticipationFullfillsParticipationRequirements(participation);
    const stateShould = requirementsMet ? ParticipationState.PARTICIPATED : ParticipationState.IN;
    if (stateShould !== participation.state) {
      await setParticipatedByID(participationID, requirementsMet);
    }
  }

  return true;
}

export const setAppointmentParticipation = registerMutation({
  operationName: 'setAppointmentParticipation',
  type: () => Boolean,
  params: [
    { name: 'participationID', type: () => String },
    { name: 'appointmentID', type: () => String },
    { name: 'fullfilled', type: () => Boolean },
  ],
  resolve: (participationID: string, appointmentID: string, fullfilled: boolean) => async () => {
    if (context.serverContext) return;
    const contextUserID = context.userID as string;
    if (!contextUserID) throw new Error('user not logged in');
    const currentUser = await getByID(User, contextUserID)({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!currentUser) throw new Error('user not logged in');
    if (
      !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION) &&
      !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION)
    )
      throw new Error('You dont have enough permission!');

    return setAppointmentParticipationServer(participationID, appointmentID, fullfilled);
  },
});

@Entity()
export class AppointmentParticipationForBulkUpdate extends AdornisEntity {
  static override _class = 'AppointmentParticipationForBulkUpdate';

  @Field(type => String) participationID: Maybe<string>;
  @Field(type => String) appointmentID: Maybe<string>;
  @Field(type => Boolean) fullfilled: Maybe<boolean>;
}

export const setAppointmentParticipationBulk = registerMutation({
  operationName: 'setAppointmentParticipationBulk',
  type: () => Boolean,
  params: [{ name: 'participations', type: () => [AppointmentParticipationForBulkUpdate] }],
  resolve: (participations: AppointmentParticipationForBulkUpdate[]) => async () => {
    if (context.serverContext) return;
    const contextUserID = context.userID as string;
    if (!contextUserID) throw new Error('user not logged in');
    const currentUser = await getByID(User, contextUserID)({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!currentUser) throw new Error('user not logged in');
    if (
      !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION) &&
      !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION)
    )
      throw new Error('You dont have enough permission!');

    bhBaseQlContext.participationStateChangeWasInitiatedByAdmin = false; // this action is intiallized by admin, but participation state should still update automatically
    await Promise.all(
      participations
        .filter(it => it.participationID && it.appointmentID)
        .map(async it => setAppointmentParticipationServer(it.participationID!, it.appointmentID!, it.fullfilled!)),
    );
  },
});

export const setParticipationRequirement = registerMutation({
  operationName: 'setParticipationRequirement',
  type: () => Boolean,
  params: [
    { name: 'participationID', type: () => String },
    { name: 'requirement', type: () => String },
    { name: 'fullfilled', type: () => Boolean },
    { name: 'throughAdmin', type: () => Boolean },
  ],
  resolve: (participationID: string, requirement: string, fullfilled: boolean, throughAdmin: boolean) => async () => {
    if (context.serverContext) return;
    const contextUserID = context.userID as string;
    if (!contextUserID) throw new Error('user not logged in');
    const currentUser = await getByID(User, contextUserID)({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!currentUser) throw new Error('user not logged in');
    if (
      !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION) &&
      !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION)
    )
      throw new Error('You dont have enough permission!');

    const participation = await getByID<typeof ParticipationWorkshop>(
      Participation as any,
      participationID,
    )({
      _id: 1,
      fulfilledParticipationRequirements: 1,
      state: 1,
      appointmentParticipations: { _id: 1, appointmentID: 1 },
      product: { settings: { extraParticipationConstraints: 1 }, appointments: { _id: 1, isNonBlocking: 1 } },
    });

    if (!participation) throw new Error(`No participation to participate in found (${participationID})`);

    // add/remove fullfilledRequirement
    participation.fulfilledParticipationRequirements = (() => {
      if (fullfilled) return [...(participation.fulfilledParticipationRequirements ?? []), requirement];
      else return (participation.fulfilledParticipationRequirements ?? []).filter(it => it !== requirement);
    })();

    // filter duplicates
    participation.fulfilledParticipationRequirements = [...new Set(participation.fulfilledParticipationRequirements)];

    // remove unknown requirements
    const allKnownRequirements = participation.product?.settings?.extraParticipationConstraints ?? [];
    participation.fulfilledParticipationRequirements = participation.fulfilledParticipationRequirements.filter(it =>
      allKnownRequirements.includes(it),
    );

    // save
    await participation.save();

    // maybe update state (and trigger sideeffects)
    if (throughAdmin) return true; // admin should be able to set state manually
    if (participation.state === ParticipationState.PARTICIPATED || participation.state === ParticipationState.IN) {
      const requirementsMet = workshopParticipationFullfillsParticipationRequirements(participation);
      const stateShould = requirementsMet ? ParticipationState.PARTICIPATED : ParticipationState.IN;
      if (stateShould !== participation.state) {
        await setParticipatedByID(participationID, requirementsMet);
      }
    }
  },
});

export async function setParticipatedByID(participationID: string, value: boolean = true) {
  const participation = await getByID<typeof ParticipationWorkshop>(
    Participation as any,
    participationID,
  )({
    _id: 1,
    productID: 1,
    userID: 1,
    state: 1,
    product: {
      _id: 1,
      settings: {
        autoSendCertifatesEnabled: 1,
      },
      attachedCertificate: {
        certificateID: 1,
      },
    },
  });
  if (!participation) throw new Error(`No participation to participate in found (${participationID})`);
  if (!participation.product) throw new Error(`product not found (${participation.productID})`);

  const state = value ? ParticipationState.PARTICIPATED : ParticipationState.IN;
  const c = await getCollection(Participation);
  await c.updateOne({ _id: participationID }, { $set: { state } });
  participation.state = state;

  const instructions = Participation.shouldSendParticipationMailAfterParticipationParticipatedGotUpdated(participation);

  const generateCertificate = async () => {
    const user = await getByID(
      User,
      participation.userID,
    )({ _id: 1, firstName: 1, title: 1, lastName: 1, email: 1, roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) throw new Error('user not found ' + participation.userID);

    const product = await getByID(
      Product,
      participation.productID,
    )({
      _class: 1,
      _id: 1,
      name: 1,
      description: 1,
      translatedDescriptionHTML: { _class: 1, langKey: 1, text: 1 },
      sortingWeight: 1,
      hasPrice: 1,
      categoryID: 1,
      institutionID: 1,
      timeframeID: 1,
      lastReminderMailSent: 1,
      publishDate: 1,
      topic: 1,
      language: 1,
      presenceType: 1,
      isCanceled: 1,
      appointments: {
        _class: 1,
        _id: 1,
        start: 1,
        end: 1,
        isNonBlocking: 1,
        location: 1,
        name: 1,
      },
      capacity: 1,
      hideCapacity: 1,
      attachedCertificate: {
        certificateID: 1,
        descriptionsHTML: TextMultipleLangItem.allFields,
        exportDate: 1,
      },
      settings: { autoSendCertifatesEnabled: 1 },
    });
    if (!product) throw new Error('product not found ' + participation.productID);

    // generate certificate
    const certificateID = product.attachedCertificate?.certificateID;
    if (!certificateID) throw new Error('No certificate found for product ' + participation.productID);

    const certificate = await getByID(
      Certificate,
      certificateID,
    )({ ...Certificate.allFields, file: { ...AdornisFile.allFields } });
    if (!certificate) throw new Error('No certificate found for ID ' + certificateID);

    await Certificate.generateCertificateFileFromParticipation(product, user);
  };

  if (instructions.shouldSendMail) {
    switch (instructions.mail) {
      case 'nutzer-teilnahme-aufgehoben':
        return await notifyNuzterTeilnahmeAufgehoben(participationID);
      case 'nutzer-teilnahme-without-certificate':
        return await notifyNuzterTeilnahme(participationID, false);
      case 'nutzer-teilnahme':
        await generateCertificate();
        return await notifyNuzterTeilnahme(participationID, true);
      default:
        throw new Error('unreachable');
    }
  }
}

// Participants can click this link to confirm that they participated at an appointment
export const getWorkshopAppointmentParticipationConfirmationLink = registerQuery({
  type: () => [WorkshopAppointmentParticipationConfirmationLink],
  operationName: 'getWorkshopAppointmentParticipationConfirmationLink',
  params: [{ type: () => String, name: 'workshopID' }],
  resolve: (workshopID: string) => {
    return async (gqlFields: BaseQLSelectionSet<WorkshopAppointmentParticipationConfirmationLink>) => {
      const workshop = await getByID(
        Workshop,
        workshopID,
      )({
        settings: {
          qrCodeValidityInMinutes: 1,
          inheritedQrCodeValidityInMinutes: 1,
        },
        appointments: {
          _id: 1,
          start: 1,
        },
      });
      const ws = workshop?.settings;
      if (!workshop) throw new Error('workshop not found ' + workshopID);
      if (!ws) throw new Error('workshop settings not found ' + workshopID);

      const links: WorkshopAppointmentParticipationConfirmationLink[] = [];
      for (const appointment of workshop.appointments) {
        const token = getAppointmentParticipationConformationSecret(workshopID, appointment);
        const url = A.absoluteUrl(
          `/extern/confirm-appointment-participation` +
            `?workshopID=${workshopID}` +
            `&appointmentID=${appointment._id}` +
            `&token=${token}`,
        );
        const validityInMinutes = parseInt(
          // @ts-expect-error clean up settings api
          ws.qrCodeValidityInMinutes ?? ws.inheritedQrCodeValidityInMinutes?.[1] ?? 60,
        );

        links.push(
          new WorkshopAppointmentParticipationConfirmationLink({
            workshopID,
            appointmentID: appointment._id,
            validFrom: appointment.start,
            validTill: appointment.start?.plus({ minutes: validityInMinutes }),
            url,
          }),
        );
      }

      return links;
    };
  },
});

export const handleWorkshopAppointmentParticipationConfirmation = registerMutation({
  operationName: 'handleWorkshopAppointmentParticipationConfirmation',
  type: () => Boolean,
  params: [
    { name: 'workshopID', type: () => String },
    { name: 'appointmentID', type: () => String },
    { name: 'token', type: () => String },
  ],
  resolve: (workshopID: string, appointmentID: string, token: string) => async () => {
    const workshop = await getByID(
      Workshop,
      workshopID,
    )({
      appointments: { _id: 1, start: 1 },
      settings: { qrCodeValidityInMinutes: 1, inheritedQrCodeValidityInMinutes: 1 },
    });
    const ws = workshop?.settings;
    if (!workshop) throw new Error('workshop not found ' + workshopID);
    if (!ws) throw new Error('workshop settings not found ' + workshopID);

    const appointment = workshop.appointments.find(it => it._id === appointmentID);
    if (!appointment) throw new Error('appointment not found ' + appointmentID);
    const user = await CurrentUserInfo.getMyself<User>()({ _id: 1, roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) throw new Error('user not logged in');
    const participations = await loadMongoData(Participation, { userID: user._id, productID: workshopID });
    if (participations.length > 1)
      throw new Error('there should be only one participation userID: ' + user._id + ' workshopID: ' + workshopID);
    const participation = participations[0];
    if (!participation) throw new Error('participation not found userID: ' + user._id + ' workshopID: ' + workshopID);

    // check for valid token
    const secret = getAppointmentParticipationConformationSecret(workshopID, appointment);
    if (secret !== token) throw new Error('invalid_token');

    // check for valid time
    if (appointment.start && DateTime.now().toMillis() < appointment.start.toMillis()) {
      throw new Error('not_valid_yet');
    }

    const validityInMinutes = parseInt(
      // @ts-expect-error clean up settings api
      ws.qrCodeValidityInMinutes ?? ws.inheritedQrCodeValidityInMinutes?.[1] ?? '0',
    );

    if (
      appointment.start &&
      validityInMinutes &&
      appointment.start.plus({ minutes: validityInMinutes }).toMillis() < DateTime.now().toMillis()
    )
      throw new Error('expired');

    // check for suspension
    if (participation.state === ParticipationState.SUSPENDED) throw new Error('participation is suspended');

    await setAppointmentParticipationServer(participation._id, appointmentID, true);

    return true;
  },
});

export const getProductParticipationConfirmationLink = registerQuery({
  type: () => ProductParticipationConfirmationLink,
  operationName: 'getProductParticipationConfirmationLink',
  params: [{ type: () => String, name: 'productID' }],
  resolve: (productID: string) => {
    return async (gqlFields: BaseQLSelectionSet<ProductParticipationConfirmationLink>) => {
      const product = await getByID(
        Product,
        productID,
      )({
        settings: {
          qrCodeValidityInMinutes: 1,
          inheritedQrCodeValidityInMinutes: 1,
        },
      });
      const ws = product?.settings;
      if (!product) throw new Error('product not found ' + productID);
      if (!ws) throw new Error('product settings not found ' + productID);

      const token = getParticipationConformationSecret(productID);
      const url = A.absoluteUrl(
        `/extern/confirm-product-participation` + `?productID=${productID}` + `&token=${token}`,
      );

      return new ProductParticipationConfirmationLink({
        productID,
        url,
      });
    };
  },
});

export const handleProductParticipationConfirmation = registerMutation({
  operationName: 'handleProductParticipationConfirmation',
  type: () => Boolean,
  params: [
    { name: 'productID', type: () => String },
    { name: 'token', type: () => String },
  ],
  resolve: (productID: string, token: string) => async () => {
    const product = await getByID(Product, productID)({});
    if (!product) throw new Error('product not found ' + productID);

    const user = await CurrentUserInfo.getMyself<User>()({ _id: 1, roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) throw new Error('user not logged in');
    const participations = await loadMongoData(Participation, { userID: user._id, productID: productID });
    if (participations.length > 1)
      throw new Error('there should be only one participation userID: ' + user._id + ' workshopID: ' + productID);
    const participation = participations[0];
    if (!participation) throw new Error('participation not found userID: ' + user._id + ' workshopID: ' + productID);

    // check for valid token
    const secret = getParticipationConformationSecret(productID);
    if (secret !== token) throw new Error('invalid_token');

    // check for suspension
    if (participation.state === ParticipationState.SUSPENDED) throw new Error('participation is suspended');

    await setParticipatedByID(participation._id, true);

    return true;
  },
});

export const countWaitingByWorkshop = registerQuery({
  operationName: 'countWaitingByWorkshop',
  params: [{ type: () => String, name: 'workshopID' }],
  type: () => Int,
  resolve: (workshopID: string) => async () => {
    const collection = await getCollection<Participation>(Participation._class);
    const count = await collection.countDocuments({
      state: {
        $in: [ParticipationState.WAITING],
      },
      productID: workshopID,
    });
    return count;
  },
});

export const subscribeParticipationsActiveByUser = registerSubscription({
  operationName: 'subscribeParticipationsActiveByUser',
  params: [{ type: () => String, name: 'userId' }],
  type: () => [Participation],
  resolve: (userID: string) => {
    return (gqlFields: BaseQLSelectionSet<Participation>) => {
      return watchQuery(Participation._collectionName, {
        $or: [
          {
            userID,
            state: { $nin: [ParticipationState.LEFT] },
          },
          {
            userID,
            state: { $in: [ParticipationState.LEFT] },
            updatedAt: { $gt: DateTime.now().minus({ days: 14 }) },
          },
        ],
      });
    };
  },
});

export const subscribeParticipationsByUserPaginated = registerSubscription({
  operationName: 'subscribeParticipationsByUserPaginated',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => BHFilter, name: 'filter' },
  ],
  type: () => [Participation],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<Participation>) => {
      if (!filter.userID) return of([]);
      return subscribePaginated(Participation, skip, limit, { userID: filter.userID });
    };
  },
});

export const subscribeParticipationsByInstitution = registerSubscription({
  operationName: 'subscribeParticipationsByInstitution',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => BHFilter, name: 'filter' },
  ],
  type: () => [Participation],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<Participation>) => {
      const institutionID = filter.institutionID;
      if (!institutionID) throw new Error('institutionID is required');

      gqlFields.productID = 1;

      const timeframeFilter = filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {};
      const searchFields = ['user.firstName', 'user.lastName', 'user.email', 'product.name'];
      const aggregation = [
        { $match: { 'product.institutionID': institutionID, ...timeframeFilter } },
        { $set: { institutionID: '$product.institutionID', timeframeID: '$product.timeframeID' } },
        { $match: searchMatch(searchFields, filter.search) },
        { $match: filter?.getMongoFilter() ?? {} },
        { $sort: filter?.getMongoOptions().sort ?? {} },
        { $skip: skip },
        { $limit: limit },
      ];

      return watchEntireCollection(`${Participation._collectionName}-materialized`).pipe(
        switchMap(() => getRawCollection(`${Participation._collectionName}-materialized`)),
        switchMap(collection =>
          collection.aggregate<Participation>(aggregation, { collation: { locale: 'de' } }).toArray(),
        ),
      );
    };
  },
});

export const getParticipationPayment = registerQuery({
  operationName: 'getParticipationPayment',
  params: [
    { type: () => String, name: 'participationID' },
    { type: () => Boolean, name: 'includeWaiting' },
  ],
  type: () => [String, Int, String],
  resolve:
    (participationID: string, includeWaiting: boolean = false) =>
    async () => {
      const user = await CurrentUserInfo.getMyself<User>()({
        _id: 1,
        email: 1,
        title: 1,
        username: 1,
        firstName: 1,
        lastName: 1,
        isLdapAccount: 1,
        whatsappNotification: 1,
        suspentions: { _id: 1, institutionID: 1 },
        whatsappPhone: 1,
      });
      if (!user) throw new Error('no user found');

      const participation = await getByID(
        Participation,
        participationID,
      )({
        _id: 1,
        productID: 1,
        state: 1,
        product: { _id: 1, institutionID: 1, prices: { unitAmount: 1 } },
        currentPayment: {
          _id: 1,
          products: {
            _id: 1,
            priceID: 1,
            product: { _id: 1, reference: 1, prices: { _id: 1, unitAmount: 1 } },
          },
        },
      });
      if (!participation) throw new Error('participation not found');

      // when waiting no payment exists, so we return the price instead (this get used on profil page but should mayne get handled diffrently)
      if (participation.state === ParticipationState.WAITING) {
        if (!includeWaiting) return undefined;
        if (!participation.product) throw new Error('product not found');
        const institutionID = participation.product?.institutionID;
        const paymentPlugin = getPluginForScope('payment', institutionID);

        const price = await paymentPlugin.getProductPrice(participation.product._id, user._id);
        return ['created', price?.unitAmount, null];
      }

      const c = await getCollection<BHPayment>(BHPayment._class);
      const payments = await c.find({ participationIDs: participationID }).toArray();
      const paymentStatusOrder: Array<PaymentStatus | SubscriptionStatus> = [
        'created',
        'issued',
        'expired',
        'failed',
        'canceled',
        'paid',
        'refund',
      ];
      let highestStatusPayment: EntityData<BHPayment> | undefined;
      payments.forEach(payment => {
        const idx = paymentStatusOrder.indexOf(payment.status);
        if (!highestStatusPayment || idx > paymentStatusOrder.indexOf(highestStatusPayment.status))
          highestStatusPayment = payment;
      });

      if (!highestStatusPayment) return undefined;

      const payment = participation.currentPayment;
      if (!payment) throw new Error('should not happen');

      // TODO: find out why sometimes basicProduct.reference and sometimes basicProduct._id is set
      const getBasicProductRef = basicProduct => basicProduct.reference ?? basicProduct._id;

      const chosenProduct = payment.products?.find(p => getBasicProductRef(p.product) === participation.productID);

      const basicProduct = chosenProduct?.product;
      if (!basicProduct) throw new Error('basicPoduct not found');
      const product = await getByID(
        Product,
        getBasicProductRef(basicProduct),
      )({ _id: 1, reference: 1, institutionID: 1, prices: { _id: 1, unitAmount: 1 } });
      if (!product) throw new Error('product not found');

      const price = chosenProduct.product.prices.find(price => price._id === chosenProduct.priceID);
      return [highestStatusPayment.status, price?.unitAmount, payment._id];
    },
});

export const removeParticipationsByUserAndInstitution = registerMutation({
  operationName: 'removeParticipationsByUserAndInstitution',
  params: [
    { type: () => String, name: 'userID' },
    { type: () => String, name: 'institutionID' },
  ],
  type: () => String,
  resolve: (userID: string, institutionID: string) => {
    return async () => {
      if (context.serverContext) return;
      const contextUserID = context.userID as string;
      if (!contextUserID) throw new Error('user not logged in');
      const currentUser = await User.getByID<User>(contextUserID)({ roles: AdornisRoleToContextsWrapper.allFields });
      if (!currentUser) throw new Error('user not logged in');
      const hasPermission = currentUser.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_USERS, institutionID);
      if (!hasPermission) throw new Error('no permission to do that');

      const collectionParticipation = await getRawCollection<Participation>(Participation._collectionName);
      const { Product } = await import('../entities/products.js');

      // remove participations
      const participationsAggregated = await collectionParticipation
        .aggregate<Participation>([
          {
            $match: {
              userID,
            },
          },
          {
            $lookup: {
              from: Product._collectionName,
              localField: 'productID',
              foreignField: '_id',
              as: 'product',
            },
          },
          {
            $match: {
              'product.institutionID': institutionID,
            },
          },
          {
            $project: {
              _id: 1,
            },
          },
        ])
        .toArray();

      const ids = participationsAggregated.map(data => data._id);

      await collectionParticipation.deleteMany({
        _id: {
          $in: ids,
        },
      });
    };
  },
});

export async function _cancelEnrollment(
  participation: NarrowedEntity<
    Participation,
    {
      _id: 1;
      productID: 1;
      state: 1;
      lastStateChangeWasInitiatedByAdmin: 1;
      timeslotID: 1;
      waitingListPlace: 1;
      nachrueckPriority: 1;
      product: {
        _id: 1;
        institutionID: 1;
        settings: { blockSignOffDaysInAdvance: 1; inheritedBlockSignOffDaysInAdvance: 1 };
      };
    }
  >,
  throughAdmin: boolean,
) {
  // check if cancel possible
  const product = participation.product;
  if (!product) throw new Error('product is not defined');

  participation.failWhenSuspended();

  const startTime = (await participation.timespan())[0]?.start;

  // only check for signoff-block when termine are defined
  if (startTime) {
    const s = product.settings;
    const blockSignOffDaysInAdvance =
      s?.blockSignOffDaysInAdvance ?? parseInt(s?.inheritedBlockSignOffDaysInAdvance?.[1] ?? '0') ?? 0;
    const lastSignOffDate = startTime.minus({ days: blockSignOffDaysInAdvance });

    // check if signoff is allowed
    // allow admin to ignore blockfrist
    if (!throughAdmin) {
      if (lastSignOffDate < DateTime.now()) {
        if (participation.state === ParticipationState.WAITING) void 0; // always allow Signoff when on waiting-list
        else throw new BlockSignoffDaysInAdvanceError();
      }
    }
  }

  // handle payment
  const paymentPlugin = getPluginForScope('payment', product.institutionID);
  await paymentPlugin.cancleEnrollment(participation._id);

  // send cancel mail
  await notifyAbmeldung({ participationID: participation._id, throughAdmin });

  // set cancel status
  if (throughAdmin) {
    bhBaseQlContext.participationStateChangeWasInitiatedByAdmin = true;
    participation.lastStateChangeWasInitiatedByAdmin = true;
  }

  // admin should be able to set state manually
  if (!throughAdmin) participation.state = ParticipationState.LEFT;

  participation.waitingListPlace = null;
  await participation.save();

  return 1;
}
